import { faSort } from '@fortawesome/free-solid-svg-icons/faSort';
import { ApiSortDirection, ApiSorting, ApiSortOption } from '@ibe/api';
import React, { JSX, useState } from 'react';
import styled from '@emotion/styled';
import Keys from '../../../_Translations/generated/service-list.en.t.json.keys';
import fallback from '../../../_Translations/generated/service-list.en.t.json';
import useTranslation from '../../Hooks/useTranslation';
import IsoDropdown from '../../Components/UI/Dropdown/Dropdown';
import { SortingProperties } from './externals';
import { ButtonGroup } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';

const SorterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0.15rem;
  padding-bottom: 0.5rem;
`;

const SortOptions = styled.div`
  max-width: 25rem;
  flex: 0 1 100%;
`;

const SorterItem = styled.div`
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 0.25rem;
  font-weight: 700;
  flex: 0;
  white-space: nowrap;
`;

export const Sorter = (sortProps: SortingProperties): JSX.Element => {
  const { t } = useTranslation('service-list', fallback, Keys);

  const { sortingType } = sortProps;

  const sortingOptions: Exclude<SortingProperties['sortingType'], undefined> = sortingType
    ? sortingType
    : { GROUP: sortProps.sortingOptions, SINGLE: [] };

  return (
    <SorterContainer className="iso__service-list__sorter">
      <SorterItem className="iso__service-list__sorter__header">{t(Keys.SortBy)}</SorterItem>
      {sortingOptions.GROUP.length ? (
        <SortOptions className="iso__service-list__sorter__options">
          <SorterElement allSortingOptions={sortingOptions.GROUP} sortProps={sortProps} />
        </SortOptions>
      ) : null}
      <ButtonGroup className="iso__service-list__sorter__container">
        {sortingOptions.SINGLE.map(sortingOption => {
          const disabledOption = sortProps.customListOptions?.disabledSortOptions?.some(
            option => option === sortingOption
          );
          return (
            <SorterElementButton
              key={sortingOption}
              sortProps={sortProps}
              sortingOption={sortingOption}
              disabled={disabledOption}
            />
          );
        })}
      </ButtonGroup>
    </SorterContainer>
  );
};

const SorterElementButton = (props: {
  sortProps: SortingProperties;
  sortingOption: ApiSortOption;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('service-list', fallback, Keys);
  const { sortProps, sortingOption } = props;

  const onButtonClick = (isActive: boolean): void => {
    if (sortProps) {
      const sorting: ApiSorting = {
        sortOption: sortingOption,
        sortDirection: !isActive
          ? ApiSortDirection.ASC
          : sortProps.sorting?.sortDirection === ApiSortDirection.ASC
          ? ApiSortDirection.DESC
          : ApiSortDirection.ASC
      };
      sortProps.onChange(sorting);
    }
  };

  const label = t(`${sortingOption.toUpperCase()}_LABEL`);
  const isActive = sortingOption === sortProps.sorting?.sortOption;
  const showUpArrow = isActive && sortProps.sorting?.sortDirection === ApiSortDirection.DESC;
  const showDownArrow = isActive && sortProps.sorting?.sortDirection === ApiSortDirection.ASC;
  return (
    <button
      className={`btn iso__service-list__sorter__button ${
        isActive ? ' iso__service-list__sorter__button--active' : ''
      } `}
      onClick={() => onButtonClick(isActive)}
      disabled={!!props.disabled}
    >
      <span></span>
      {label}
      {showUpArrow && (
        <FontAwesomeIcon className="iso__service-list__sorter__icon" icon={faChevronUp} />
      )}
      {showDownArrow && (
        <FontAwesomeIcon className="iso__service-list__sorter__icon" icon={faChevronDown} />
      )}
    </button>
  );
};

const SorterElement = (props: {
  sortProps: SortingProperties;
  allSortingOptions: SortingProperties['sortingOptions'];
}): JSX.Element => {
  const { sortProps, allSortingOptions } = props;
  const [sortOpen, setSortOpen] = useState(false);
  const { t } = useTranslation('service-list', fallback, Keys);
  const matches = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

  const [sorter, setSorter] = useState<ApiSorting | undefined>(
    sortProps.sorting || sortProps.defaultSorting
  );

  const onSorterChange = (sortDirection: ApiSortDirection, sortOption: ApiSortOption): void => {
    const sorting = { sortDirection, sortOption };
    setSorter(sorting);
    if (sortProps) {
      sortProps.onChange(sorting);
    }
    if (sortProps.customListOptions?.closeSorterOnClick) {
      setSortOpen(false);
    }
  };

  return (
    <IsoDropdown
      isOpen={sortOpen}
      onDropdownToggle={setSortOpen}
      icon={!sortProps.customListOptions?.sortingSimpleLabel || matches ? faSort : undefined}
      tabIndex={null}
      title={
        <>
          {sortProps.customListOptions?.customIcons?.sort && !matches && (
            <FontAwesomeIcon icon={sortProps.customListOptions?.customIcons?.sort} />
          )}
          {sorter &&
          sorter.sortDirection &&
          (!sortProps.customListOptions?.sortingSimpleLabel ||
            (matches && sortProps.customListOptions?.sortingSimpleLabel === 'MOBILE'))
            ? `${
                matches && sortProps.customListOptions?.sortingSimpleLabel === 'MOBILE'
                  ? `${t(Keys.PleaseSelect)} `
                  : ''
              }${t(`${sorter.sortOption}_${sorter.sortDirection.toUpperCase()}`)}`
            : t(Keys.PleaseSelect)}
        </>
      }
    >
      {allSortingOptions.map(sortingOption => (
        <>
          <span
            tabIndex={0}
            role="menuitem"
            key={sortingOption.toString() + '_ASC'}
            onClick={(): void => onSorterChange(ApiSortDirection.ASC, ApiSortOption[sortingOption])}
            className={`dropdown-item${
              (sorter?.sortOption === sortingOption && sorter?.sortDirection) ===
              ApiSortDirection.ASC
                ? ' dropdown-item--selected'
                : ''
            }`}
          >
            {t(`${sortingOption.toUpperCase()}_${ApiSortDirection.ASC.toUpperCase()}`)}
          </span>

          {sortingOption !== ApiSortOption.PROMOTIONS && (
            <span
              tabIndex={0}
              role="menuitem"
              key={sortingOption.toString() + '_DESC'}
              onClick={(): void =>
                onSorterChange(ApiSortDirection.DESC, ApiSortOption[sortingOption])
              }
              className={`dropdown-item${
                sorter?.sortOption === sortingOption &&
                sorter?.sortDirection === ApiSortDirection.DESC
                  ? ' dropdown-item--selected'
                  : ''
              }`}
            >
              {t(`${sortingOption.toUpperCase()}_${ApiSortDirection.DESC.toUpperCase()}`)}
            </span>
          )}
        </>
      ))}
    </IsoDropdown>
  );
};
